import { env } from './env';
import { clearStoredSession, markPasswordChangeRequired } from './session';

/**
 * Thin fetch wrapper over the real API, `/api/v1`-prefixed — identical to
 * apps/merchant-dashboard's own api-client.ts. No `shopId`/`X-Shop-Id`
 * parameter here: every `/api/v1/admin/*` route is structurally without a
 * shop (`RequirePlatformRole`'s own doc comment — "not /merchant/*"), so
 * this app never has one to attach.
 */

/**
 * DT-4 / P1-2, revised: `AuthenticatedGuard`
 * (apps/api/src/modules/identity/http/authenticated.guard.ts) is the ONLY
 * place a 401 means "this session's token is no longer good — sign in
 * again". Every other 401 this API returns is a handler's own deliberate,
 * recoverable rejection with a token that is still perfectly valid:
 * `POST /auth/login` with the wrong password sends no token at all, and a
 * wrong « confirm your password » on a critical action (403
 * `REAUTH_FAILED`) is sent with a real, still-valid token.
 *
 * This used to be told apart by matching the exact English string
 * `'Authentication required.'` — fragile the moment that message changed
 * for any reason, including translation. The API now attaches a stable
 * `errorCode: 'SESSION_EXPIRED'` (`AllExceptionsFilter`,
 * `SessionExpiredException`) for exactly this condition; that code, never
 * the message text, is what the check below matches on.
 */
const SESSION_EXPIRED_ERROR_CODE = 'SESSION_EXPIRED';

/**
 * The account holds a PROVISIONAL password (issued by a superadmin): the API refuses every route but the password change with this
 * code. The session is not dead - the person must choose a password - so it goes to that screen, not to the login.
 */
const PASSWORD_CHANGE_REQUIRED_ERROR_CODE = 'PASSWORD_CHANGE_REQUIRED';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown,
    /**
     * The stable code `@ecom/i18n`'s `resolveErrorMessageKey()` maps to a
     * translated, business-appropriate message — see that module's header
     * for why this exists instead of showing `.message` (the API's raw,
     * untranslated free text) to the user. `undefined` for a response with
     * no JSON body, or one from a server old enough not to send it yet.
     */
    readonly errorCode?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiRequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly accessToken?: string | null;
  readonly signal?: AbortSignal;
}

export async function apiRequest<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const { method = 'GET', body, accessToken, signal } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

  // `exactOptionalPropertyTypes` forbids `signal: undefined` / `body: undefined`
  // as explicit values — RequestInit's own properties must be absent, not
  // present-with-undefined — hence building this object conditionally.
  const init: RequestInit = {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...(signal !== undefined ? { signal } : {}),
  };

  const response = await fetch(`${env.apiBaseUrl}/api/v1${path}`, init);

  const isJson = response.headers.get('content-type')?.includes('application/json') ?? false;
  const payload: unknown = isJson ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    const isPayloadObject = payload !== null && typeof payload === 'object';
    const message =
      isPayloadObject && 'message' in payload
        ? String(payload.message)
        : `Request failed with status ${String(response.status)}.`;
    const errorCode =
      isPayloadObject && 'errorCode' in payload ? String(payload.errorCode) : undefined;

    // DT-4 / P1-2 — a dead session purges itself and leaves for /login
    // instead of every page it still touches staring at a stuck skeleton
    // or a bare "Authentication required." error forever (reproduced
    // live: the token invalidated, `RequireAuth` never re-checks because
    // it only asks "is a token PRESENT", and nothing else in this app
    // was watching for a 401 to say otherwise).
    if (response.status === 401 && errorCode === SESSION_EXPIRED_ERROR_CODE) {
      clearStoredSession();
      if (window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    }

    if (response.status === 403 && errorCode === PASSWORD_CHANGE_REQUIRED_ERROR_CODE) {
      markPasswordChangeRequired();
      if (window.location.pathname !== '/change-password') {
        window.location.assign('/change-password');
      }
    }

    throw new ApiError(message, response.status, payload, errorCode);
  }

  return payload as T;
}

/**
 * Downloads a FILE the API answers with (the audit export, a CSV): the same authenticated request as `apiRequest`, but the body is kept as
 * a Blob. A refusal is the same `ApiError` as everywhere (its JSON body is read), so a screen translates it with the same resolver.
 */
export async function apiDownload(path: string, accessToken: string | null): Promise<Blob> {
  const response = await fetch(`${env.apiBaseUrl}/api/v1${path}`, {
    method: 'GET',
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
  });

  if (!response.ok) {
    const isJson = response.headers.get('content-type')?.includes('application/json') ?? false;
    const payload: unknown = isJson ? await response.json().catch(() => null) : null;
    const isPayloadObject = payload !== null && typeof payload === 'object';
    throw new ApiError(
      isPayloadObject && 'message' in payload
        ? String(payload.message)
        : `Request failed with status ${String(response.status)}.`,
      response.status,
      payload,
      isPayloadObject && 'errorCode' in payload ? String(payload.errorCode) : undefined,
    );
  }
  return response.blob();
}
